// src/components/QRLabelModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { jsPDF } from "jspdf";
import { supabase } from "../utils/supabase";
import { permanentInstrumentQrValue } from "../utils/instrument-admission";

export default function QRLabelModal({
  open,
  onClose,
  instrument, // { id, labId/lab_id, tag, type, qrImageUrl, ... }
  clientName,
  poCode,
}) {
  const [busy, setBusy] = useState(false);
  const [qrPreview, setQrPreview] = useState(null); // dataURL para <img>

  const dateStr = useMemo(() => {
    try {
      return new Date().toLocaleDateString("es-AR");
    } catch {
      return "";
    }
  }, []);

  // Cerrar con ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Generar preview del QR siempre que se abre
  useEffect(() => {
    if (!open) return;
    setQrPreview(null);

    (async () => {
      const payload = getInstrumentPayload(instrument);
      if (!payload) {
        console.warn(
          "[QRLabelModal] Instrumento sin identificador para QR",
          instrument
        );
        return;
      }

      try {
        const dataUrl = await generateQrDataUrl(payload);
        setQrPreview(dataUrl);
        return;
      } catch (err) {
        console.error("[QRLabelModal] error generando QR:", err);
      }

      // Fallback extremo: si falla el generador, usamos el PNG guardado (si existe)
      if (instrument?.qrImageUrl) {
        try {
          const blob = await fetch(instrument.qrImageUrl).then((r) => r.blob());
          const dataUrl = await blobToDataURL(blob);
          setQrPreview(dataUrl);
        } catch (e) {
          console.error(
            "[QRLabelModal] fallback qrImageUrl también falló:",
            e
          );
        }
      }
    })();
  }, [open, instrument]);

  if (!open) return null;

  const payloadForView = getInstrumentPayload(instrument);

  // Descarga del PNG del QR (solo QR)
  const handleDownloadPNG = async () => {
    try {
      setBusy(true);
      const payload = getInstrumentPayload(instrument);
      if (!payload)
        throw new Error("El instrumento no tiene identificador para QR.");

      let dataUrl = qrPreview;
      if (!dataUrl) {
        dataUrl = await generateQrDataUrl(payload);
      }
      if (!dataUrl) throw new Error("No se pudo generar el QR.");

      downloadDataUrl(dataUrl, `QR_${payload}.png`);
      await markQrPrinted(instrument);
    } catch (e) {
      alert(e?.message || "No se pudo descargar el PNG");
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  // Descargar PDF etiqueta
  const handleDownloadPDF = async () => {
    try {
      setBusy(true);
      await buildAndDownloadPdf({ instrument, qrPreview });
      await markQrPrinted(instrument);
    } catch (e) {
      alert(e?.message || "No se pudo generar el PDF");
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  // Imprimir directamente la etiqueta
  const handlePrintLabel = async () => {
    try {
      setBusy(true);

      const blob = await buildPdfBlob({ instrument, qrPreview });

      const url = URL.createObjectURL(blob);
      const win = window.open("");
      if (!win) {
        alert(
          "El navegador bloqueó la ventana de impresión. Permití las ventanas emergentes para esta página."
        );
        URL.revokeObjectURL(url);
        return;
      }

      win.document.write(`<!doctype html>
<html>
  <head><title>Imprimir etiqueta</title></head>
  <body style="margin:0">
    <iframe
      src="${url}"
      style="border:0;width:100%;height:100vh;"
      onload="this.contentWindow.focus();this.contentWindow.print();"
    ></iframe>
  </body>
</html>`);
      win.document.close();

      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      await markQrPrinted(instrument);
    } catch (e) {
      alert(e?.message || "No se pudo preparar la impresión de la etiqueta");
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
    >
      <div className="w-full max-w-xl rounded-2xl bg-[#12161a] p-5 border border-white/10 shadow-xl">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Etiqueta del instrumento</h3>
          <button
            onClick={onClose}
            className="px-2 py-1 text-sm rounded hover:bg-white/10"
          >
            Cerrar
          </button>
        </div>

        <div className="mt-4 grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-6">
            <div className="rounded-lg border border-white/10 p-3 flex flex-col items-center justify-center h-48 bg-black/20">
              {qrPreview ? (
                <>
                  <img
                    src={qrPreview}
                    alt="QR"
                    className="max-h-40 object-contain mb-1"
                  />
                  {payloadForView && (
                    <div className="flex flex-col items-center gap-0.5">
                      <span className="text-sm text-white/90 font-semibold">
                        ID: {payloadForView}
                      </span>
                      <span className="text-sm text-white font-bold">
                        Calins
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm text-white/60">
                  Generando vista previa…
                </div>
              )}
            </div>
          </div>

          <div className="col-span-12 md:col-span-6 space-y-2">
            <KV
              label="ID Lab"
              value={`#${instrument?.labId ?? instrument?.lab_id ?? "—"}`}
            />
            <KV label="Tag" value={instrument?.tag || "—"} />
            <KV label="Tipo" value={instrument?.type || "—"} />
            <KV label="Cliente" value={clientName || "—"} />
            <KV label="OC" value={poCode || "—"} />
            <KV label="Fecha" value={dateStr} />

            <div className="pt-2 flex flex-wrap gap-2">
              <button
                onClick={handlePrintLabel}
                disabled={busy}
                className="px-4 py-2 rounded bg-emerald-600 text-white disabled:opacity-60"
              >
                {busy ? "Imprimiendo…" : "Imprimir etiqueta"}
              </button>

              <button
                onClick={handleDownloadPDF}
                disabled={busy}
                className="px-4 py-2 rounded bg-sky-600 text-white disabled:opacity-60"
              >
                {busy ? "Generando…" : "Descargar PDF"}
              </button>

              <button
                onClick={handleDownloadPNG}
                disabled={busy}
                className="px-4 py-2 rounded border border-white/20 hover:bg-white/10 disabled:opacity-60"
              >
                {busy ? "Generando…" : "Descargar PNG"}
              </button>

              {instrument?.qrImageUrl && (
                <a
                  href={instrument.qrImageUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-4 py-2 rounded border border-white/20 hover:bg-white/10"
                >
                  Ver PNG original
                </a>
              )}
            </div>
          </div>
        </div>

        <p className="mt-3 text-xs text-white/50">
          Este es el QR permanente del instrumento. La vista puede volver a
          dibujarse o imprimirse, pero conserva el mismo identificador (ID Lab o,
          si no existe, el ID interno / Tag).
        </p>
      </div>
    </div>
  );
}

/* ───────────────── Helpers de datos ───────────────── */

function getInstrumentPayload(instrument) {
  return permanentInstrumentQrValue(instrument);
}

async function markQrPrinted(instrument) {
  if (!instrument?.id) return;

  const { data: authData } = await supabase.auth.getUser();
  const { error } = await supabase
    .from("instruments")
    .update({
      qr_printed_at: new Date().toISOString(),
      qr_printed_by: authData?.user?.id || null,
    })
    .eq("id", instrument.id);

  if (error) {
    console.warn("[QRLabelModal] no se pudo registrar impresion QR:", error);
  }
}

/* ───── Generador de QR ───── */

async function generateQrDataUrl(payload) {
  if (!payload) throw new Error("Payload vacío para QR.");

  // margin=1 → quiet zone razonable sin agrandar demasiado
  return QRCode.toDataURL(String(payload), {
    errorCorrectionLevel: "H",
    scale: 8,
    margin: 1,
  });
}

/* ───── PDF etiqueta COMPACTA (texto ABAJO del QR) ─────
   - Cinta: 18mm (alto)
   - Largo etiqueta: 20mm (compacto)
   - Margen: 2mm (mínimo)
*/
const LABEL_W_MM = 20; // largo (feed)
const LABEL_H_MM = 18; // ancho cinta (alto)
const MARGIN_MM = 2;

const QR_SIZE_MM = 9; // QR más chico
const QR_X_MM = (LABEL_W_MM - QR_SIZE_MM) / 2; // centrado horizontal
const QR_Y_MM = MARGIN_MM; // arriba con margen 2mm

// Textos debajo del QR (compactos)
const ID_Y_MM = QR_Y_MM + QR_SIZE_MM + 3.0;
const CALINS_Y_MM = ID_Y_MM + 3.2;

function drawCompactBelow(doc, payload, dataUrl) {
  // QR arriba
  doc.addImage(dataUrl, "PNG", QR_X_MM, QR_Y_MM, QR_SIZE_MM, QR_SIZE_MM);

  // ID abajo (centrado)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text(`ID: ${payload}`, LABEL_W_MM / 2, ID_Y_MM, { align: "center" });

  // Calins abajo del ID (centrado)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("Calins", LABEL_W_MM / 2, CALINS_Y_MM, { align: "center" });
}

// Descargar PDF
async function buildAndDownloadPdf({ instrument, qrPreview }) {
  const payload = getInstrumentPayload(instrument);
  if (!payload) throw new Error("El instrumento no tiene identificador para QR.");

  let dataUrl = qrPreview;
  if (!dataUrl) dataUrl = await generateQrDataUrl(payload);
  if (!dataUrl) throw new Error("No se pudo generar el QR.");

  const doc = new jsPDF({ unit: "mm", format: [LABEL_W_MM, LABEL_H_MM] });
  drawCompactBelow(doc, payload, dataUrl);
  doc.save(`QR_${payload}.pdf`);
}

// Para imprimir (devuelve Blob)
async function buildPdfBlob({ instrument, qrPreview }) {
  const payload = getInstrumentPayload(instrument);
  if (!payload) throw new Error("El instrumento no tiene identificador para QR.");

  let dataUrl = qrPreview;
  if (!dataUrl) dataUrl = await generateQrDataUrl(payload);
  if (!dataUrl) throw new Error("No se pudo generar el QR.");

  const doc = new jsPDF({ unit: "mm", format: [LABEL_W_MM, LABEL_H_MM] });
  drawCompactBelow(doc, payload, dataUrl);
  return doc.output("blob");
}

/**
 * Renderiza la etiqueta completa (QR + textos) ya ROTADA 90°,
 * respetando margen mínimo de 2mm, y devolviendo un PNG.
 */
async function renderRotatedLabelPng({ payload, qrDataUrl }) {
  // Resolución para impresión: 300dpi aprox
  const pxPerMm = 300 / 25.4;

  const W = Math.round(LABEL_W_MM * pxPerMm);
  const H = Math.round(LABEL_H_MM * pxPerMm);

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Fondo blanco
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, W, H);

  // Convert mm -> px
  const mm = (v) => Math.round(v * pxPerMm);

  const marginPx = mm(MARGIN_MM);
  const qrPx = mm(QR_MM);
  const gapPx = mm(GAP_MM);

  // Cargamos QR como imagen
  const qrImg = await loadImage(qrDataUrl);

  // Dibujamos TODO rotado 90° en bloque:
  // 1) Dibujamos un "layout base" horizontal dentro de un espacio virtual (alto=18, ancho=24)
  // 2) Lo rotamos para que quede en el canvas 18×24 final.
  //
  // Coordenadas base (antes de rotar):
  // - QR a la izquierda
  // - Texto a la derecha
  const baseW = H; // 24mm -> en px (porque vamos a rotar)
  const baseH = W; // 18mm -> en px

  // Creamos un canvas temporal "base" (horizontal), luego lo rotamos al final.
  const base = document.createElement("canvas");
  base.width = baseW;
  base.height = baseH;
  const b = base.getContext("2d");

  b.fillStyle = "#FFFFFF";
  b.fillRect(0, 0, baseW, baseH);

  // Márgenes en el canvas base (mismos 2mm)
  const bm = marginPx;

  // Posiciones base (horizontal)
  const qrX = bm;
  const qrY = Math.round((baseH - qrPx) / 2);

  // QR (sin rotar en base)
  b.drawImage(qrImg, qrX, qrY, qrPx, qrPx);

  // Texto (a la derecha del QR)
  const textX = qrX + qrPx + gapPx;

  // Fuente: en px
  const fontPx = mm(FONT_MM);
  b.fillStyle = "#000000";
  b.font = `700 ${fontPx}px Arial`;
  b.textBaseline = "alphabetic";

  // Compactamos el texto y lo centramos verticalmente en el alto base
  const lineGap = Math.round(fontPx * 1.15);
  const blockH = lineGap * 2;

  const textY1 = Math.round(baseH / 2 - blockH / 2 + fontPx);
  const textY2 = textY1 + lineGap;

  b.fillText(`ID: ${payload}`, textX, textY1);
  b.fillText("Calins", textX, textY2);

  // Ahora rotamos TODO el canvas base 90° para que quede como querés (opción B).
  // Rotación 90° horario: (baseW×baseH) -> (W×H)
  ctx.save();
  ctx.translate(W, 0);
  ctx.rotate(Math.PI / 2);
  ctx.drawImage(base, 0, 0);
  ctx.restore();

  return canvas.toDataURL("image/png");
}

/* ───────────────── Otros helpers ───────────────── */

function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.crossOrigin = "anonymous";
    img.src = src;
  });
}

function KV({ label, value }) {
  return (
    <div className="text-sm">
      <span className="text-white/60">{label}:</span>{" "}
      <span className="font-medium">{value}</span>
    </div>
  );
}
